import React, { useRef, useState } from "react";
import api, { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { X, Paperclip, DotsSixVertical } from "@phosphor-icons/react";

const MAX_DIM = 1920;   // largest edge for images
const JPEG_QUALITY = 0.85;

/** Client-side image compression via <canvas>. Returns a Blob (JPEG) or original if not a compressible image. */
async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      c.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) resolve(file);
          else resolve(new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}

/** Extract first-frame poster from a video file. Returns a File (JPEG) or null. */
async function extractVideoPoster(file) {
  if (!file.type.startsWith("video/")) return null;
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => { try { URL.revokeObjectURL(video.src); } catch (_e) { /* ignore */ } };
    video.onloadedmetadata = () => { video.currentTime = Math.min(1, video.duration / 4); };
    video.onseeked = () => {
      try {
        const scale = Math.min(1, MAX_DIM / Math.max(video.videoWidth, video.videoHeight));
        const w = Math.round(video.videoWidth * scale);
        const h = Math.round(video.videoHeight * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(video, 0, 0, w, h);
        c.toBlob(
          (blob) => { cleanup(); resolve(blob ? new File([blob], "poster.jpg", { type: "image/jpeg" }) : null); },
          "image/jpeg", 0.8,
        );
      } catch { cleanup(); resolve(null); }
    };
    video.onerror = () => { cleanup(); resolve(null); };
    video.src = URL.createObjectURL(file);
  });
}

export default function PhotoAttacher({ value = [], onChange, accept = "image/*", label = "Attach photos" }) {
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);
  const token = getToken();

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newIds = [];
    for (const raw of files) {
      try {
        const isVideo = raw.type.startsWith("video/");
        const finalFile = isVideo ? raw : await compressImage(raw);
        const fd = new FormData();
        fd.append("file", finalFile);
        const { data: parent } = await api.post("/memories/upload?caption=", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(parent.id);
        // For videos, extract & upload a poster
        if (isVideo) {
          const posterFile = await extractVideoPoster(raw);
          if (posterFile) {
            const pfd = new FormData();
            pfd.append("file", posterFile);
            await api.post(`/memories/upload?caption=&poster_of=${parent.id}`, pfd, { headers: { "Content-Type": "multipart/form-data" } });
          }
        }
      } catch { toast.error(`Upload failed: ${raw.name}`); }
    }
    if (newIds.length) {
      onChange([...(value || []), ...newIds]);
      toast.success(`${newIds.length} file${newIds.length > 1 ? "s" : ""} attached`);
    }
    if (inputRef.current) inputRef.current.value = "";
    setUploading(false);
  };

  const remove = (id) => onChange((value || []).filter((x) => x !== id));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const arr = [...value];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(i, 0, moved);
    setDragIdx(i);
    onChange(arr);
  };
  const onDragEnd = () => setDragIdx(null);

  const url = (id) => `${API_BASE}/memories/file/${id}?auth=${token}`;

  return (
    <div data-testid="photo-attacher">
      <input ref={inputRef} type="file" accept={accept} multiple onChange={upload} className="hidden" id={`attach-${label.replace(/\s/g, "-")}`} data-testid="attacher-input" />
      <label htmlFor={`attach-${label.replace(/\s/g, "-")}`} className="btn-secondary cursor-pointer inline-flex items-center gap-2" data-testid="attacher-btn">
        <Paperclip weight="thin" size={16} /> {uploading ? "Processing…" : label}
      </label>
      {value && value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="attacher-previews">
          {value.map((id, i) => (
            <div
              key={id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDragEnd={onDragEnd}
              className={`relative w-20 h-20 rounded-lg overflow-hidden bg-vansha-accent group cursor-move ${dragIdx === i ? "opacity-40" : ""}`}
              data-testid={`attach-preview-${id}`}
              title="Drag to reorder"
            >
              <SmartMedia id={id} className="w-full h-full object-cover pointer-events-none" />
              <div className="absolute top-1 left-1 bg-white/80 backdrop-blur rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                <DotsSixVertical weight="bold" size={12} className="text-ink-700" />
              </div>
              <button type="button" onClick={() => remove(id)} className="absolute top-1 right-1 bg-white/90 backdrop-blur rounded-full p-1 opacity-0 group-hover:opacity-100 transition" data-testid={`remove-${id}`}>
                <X weight="bold" size={12} className="text-terracotta" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Module-level metadata cache to avoid N+1 requests when rendering multiple SmartMedia
const _metaCache = new Map();
let _metaPromise = null;
function fetchAllMeta() {
  if (_metaPromise) return _metaPromise;
  _metaPromise = api.get("/memories").then((r) => {
    r.data.forEach((m) => _metaCache.set(m.id, m));
    return _metaCache;
  }).catch(() => _metaCache);
  return _metaPromise;
}
export function invalidateMediaCache() { _metaCache.clear(); _metaPromise = null; }

function SmartMedia({ id, className = "" }) {
  const token = getToken();
  const [state, setState] = React.useState(_metaCache.get(id)?.content_type?.startsWith("video/") ? "video" : "img");
  const [meta, setMeta] = React.useState(_metaCache.get(id) || null);
  const src = `${API_BASE}/memories/file/${id}?auth=${token}`;

  React.useEffect(() => {
    if (_metaCache.has(id)) return;
    let mounted = true;
    fetchAllMeta().then(() => {
      if (!mounted) return;
      const m = _metaCache.get(id);
      setMeta(m);
      if (m?.content_type?.startsWith("video/")) setState("video");
    });
    return () => { mounted = false; };
  }, [id]);

  if (state === "video") {
    const posterUrl = meta?.poster_memory_id ? `${API_BASE}/memories/file/${meta.poster_memory_id}?auth=${token}` : undefined;
    return <video src={src} poster={posterUrl} controls className={className} data-testid={`smart-video-${id}`} />;
  }
  return <img src={src} alt="" onError={() => setState("video")} className={className} loading="lazy" data-testid={`smart-img-${id}`} />;
}

export function AttachedMedia({ ids = [] }) {
  if (!ids || ids.length === 0) return null;
  return (
    <div className={`grid gap-2 mt-3 ${ids.length === 1 ? "grid-cols-1" : ids.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="attached-media">
      {ids.map((id) => (
        <div key={id} className="rounded-xl overflow-hidden bg-vansha-accent aspect-square" data-testid={`media-${id}`}>
          <SmartMedia id={id} className="w-full h-full object-cover" />
        </div>
      ))}
    </div>
  );
}
